import { IColumnHeaderParams, IPage, IPageRange } from '@aon/aon-tws-common-ui';
import { cloneDeep as _cloneDeep } from 'lodash';
import { push } from 'react-router-redux';
import { Reducer } from 'redux';
import { AppDispatch } from '../appStore';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import {
	IColumn,
	ISurveyDeleteStatusVm,
	ISurveyListItem,
	IValidationModel,
	SurveyDeleteStatusType,
	SurveyStatusType,
	SurveyStep,
} from '../models';
import * as SurveyAPI from '../services/SurveyAPI';
import * as SurveyColumnAPI from '../services/SurveyColumnAPI';
import * as SurveyListAPI from '../services/SurveyListAPI';
import { SurveyBuilderHelper } from '../utilities';
import { ActionTypes } from './ActionTypes';
import {
	ISurveyDeleteChanged,
	ISurveyListClearValidation,
	ISurveyListCloneSurveyRequested,
	ISurveyListCloneSurveySucceeded,
	ISurveyListColumnsGetRequested,
	ISurveyListColumnsGetSucceeded,
	ISurveyListColumnsUpdate,
	ISurveyListDeleteSurveyFailed,
	ISurveyListDeleteSurveyRequested,
	ISurveyListDeleteSurveySucceeded,
	ISurveyListGetFailed,
	ISurveyListGetRequested,
	ISurveyListGetSucceeded,
	ISurveyListUpdateSurveyName,
} from './Actions';

export interface ISurveyListState {
	columns: IColumn[];
	deleteStatusChange?: ISurveyDeleteStatusVm;
	loading: boolean;
	pageRange: IPageRange;
	sortParams: IColumnHeaderParams;
	surveys: IValidationModel<ISurveyListItem>[];
}

export const defaultState: ISurveyListState = {
	columns: [],
	loading: false,
	pageRange: { offset: 0, limit: 25 },
	sortParams: {
		columns: [],
		sortedBy: 'Create Date',
		sortAsc: true,
	},
	surveys: [],
};

type KnownActions =
	| ISurveyListClearValidation
	| ISurveyListCloneSurveyRequested
	| ISurveyListCloneSurveySucceeded
	| ISurveyListColumnsGetRequested
	| ISurveyListColumnsGetSucceeded
	| ISurveyListColumnsUpdate
	| ISurveyListDeleteSurveyFailed
	| ISurveyListDeleteSurveyRequested
	| ISurveyListDeleteSurveySucceeded
	| ISurveyListGetRequested
	| ISurveyListGetSucceeded
	| ISurveyListGetFailed
	| ISurveyListUpdateSurveyName
	| ISurveyDeleteChanged;

export const ActionCreators = {
	GetSurveyColumns: () => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_COLUMNS_GET_REQUESTED });

		return SurveyColumnAPI.getColumns().then((columns: IColumn[]) =>
			dispatch({ type: ActionTypes.SURVEY_LIST_COLUMNS_GET_SUCCEEDED, payload: columns }),
		);
	},
	GetSurveys: () => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_GET_REQUESTED });

		return SurveyListAPI.getSurveys()
			.then((pagedResult: IPage<ISurveyListItem[]>) =>
				dispatch({
					type: ActionTypes.SURVEY_LIST_GET_SUCCEEDED,
					payload: pagedResult.items,
				}),
			)
			.catch((error: Error) => {
				dispatch({ type: ActionTypes.SURVEY_LIST_GET_FAILED });
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: error.message });
			});
	},
	UpdateColumns: (columns: IColumn[]) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_COLUMNS_UPDATE, payload: columns });
	},
	BuildNewSurvey: () => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_SET_DEFAULT_STATE });
		dispatch(push(Constants.ROUTE_BASICS));
	},
	GoToSurvey: (surveyId: number, surveyStatus: SurveyStatusType) => (dispatch: AppDispatch) => {
		if (surveyStatus === SurveyStatusType.Draft) {
			SurveyBuilderHelper.route(dispatch, SurveyStep.Basics, surveyId, false);
			return;
		}

		if (surveyStatus !== SurveyStatusType.Failed) {
			SurveyBuilderHelper.route(dispatch, SurveyStep.Admin, surveyId, false);
			return;
		}

		dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

		SurveyAPI.getSurveyStatusLogs(surveyId)
			.then((surveyStatusLogs) => {
				const hasBeenPreviouslyPublished = surveyStatusLogs.some(
					(surveyStatusLog) =>
						surveyStatusLog.surveyStatus === SurveyStatusType.Scheduled ||
						surveyStatusLog.surveyStatus === SurveyStatusType.SurveyInProgress ||
						surveyStatusLog.surveyStatus === SurveyStatusType.ClosingInProgress ||
						surveyStatusLog.surveyStatus === SurveyStatusType.SurveyCloseFailed ||
						surveyStatusLog.surveyStatus === SurveyStatusType.SurveyClosed,
				);

				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });

				if (hasBeenPreviouslyPublished) {
					SurveyBuilderHelper.route(dispatch, SurveyStep.Admin, surveyId, false);
					return;
				}

				SurveyBuilderHelper.route(dispatch, SurveyStep.Basics, surveyId, false);
			})
			.catch(() => {
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_ERROR_GENERIC_LOAD_BODY });
			});
	},
	GoToSurveyAdmin: (surveyId: number) => (dispatch: AppDispatch) => {
		SurveyBuilderHelper.route(dispatch, SurveyStep.Admin, surveyId, false);
	},
	CloneSurvey: (surveyId: number) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_CLONE_SURVEY_REQUESTED });

		return SurveyAPI.cloneSurvey(surveyId)
			.then((survey: ISurveyListItem) => {
				dispatch({ type: ActionTypes.SURVEY_LIST_CLONE_SURVEY_SUCCEEDED, payload: [survey] });
			})
			.catch(() => ({ type: ActionTypes.SURVEY_LIST_CLONE_SURVEY_FAILED }));
	},
	DeleteSurvey: (surveyId: number) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_DELETE_SURVEY_REQUESTED, payload: surveyId });
		return SurveyAPI.deleteSurvey(surveyId)
			.then(() => {
				dispatch({ type: ActionTypes.SURVEY_LIST_DELETE_SURVEY_SUCCEEDED });
				dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
			})
			.catch((error) => {
				dispatch({ type: ActionTypes.SURVEY_LIST_DELETE_SURVEY_FAILED, payload: surveyId });
				dispatch({ type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION, payload: { message: error.message, messageTitle: 'Error' } });
			});
	},
	NotifyDeleteStatus: (deleteStatusChange: ISurveyDeleteStatusVm) => (dispatch: AppDispatch) => {
		switch (deleteStatusChange.surveyDeleteStatus) {
			case SurveyDeleteStatusType.Error: {
				const messageText = 'Could not delete survey {surveyId}.';
				const replacementText = messageText.replace('{surveyId}', deleteStatusChange.surveyId.toString());
				dispatch({
					type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: { message: replacementText, messageTitle: 'Error' },
				});
				break;
			}
			case SurveyDeleteStatusType.Complete: {
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
				dispatch(ActionCreators.GetSurveys());
				break;
			}
			case SurveyDeleteStatusType.InProgress: {
				dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
				break;
			}
			case SurveyDeleteStatusType.NotStarted:
			default:
				//do nothing
				break;
		}
	},
	UpdateSurveyName: (listItem: ISurveyListItem) => (dispatch: AppDispatch) => {
		return SurveyListAPI.updateSurveyName(listItem).then((responseListItem: IValidationModel<ISurveyListItem>) =>
			dispatch({ type: ActionTypes.SURVEY_LIST_UPDATE_SURVEY_NAME, payload: responseListItem }),
		);
	},
	ClearValidation: (listItemToClear: ISurveyListItem) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.SURVEY_LIST_CLEAR_VALIDATION, payload: listItemToClear });
	},
	EditSurveyTemplate: (surveyId: number) => (dispatch: AppDispatch) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
		return SurveyAPI.setTemplateStatus(surveyId, SurveyStatusType.Draft)
			.then(() => {
				dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
				SurveyBuilderHelper.route(dispatch, SurveyStep.Basics, surveyId, false);
			})
			.catch(() => {
				dispatch({ type: ActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_ERROR_GENERIC_SAVE_BODY });
			});
	},
};

export const reducer: Reducer<ISurveyListState, KnownActions> = (state: ISurveyListState | undefined, action: KnownActions) => {
	if (!state) {
		state = { ...defaultState };
	}
	switch (action.type) {
		case ActionTypes.SURVEY_LIST_GET_REQUESTED:
			return { ...state, loading: true };

		case ActionTypes.SURVEY_LIST_GET_FAILED:
			return { ...state, loading: false };

		case ActionTypes.SURVEY_LIST_GET_SUCCEEDED:
			return {
				...state,
				surveys: action.payload.map((subject) => ({ subject, isValid: true })),
				loading: false,
			};

		// Columns
		case ActionTypes.SURVEY_LIST_COLUMNS_GET_SUCCEEDED:
		case ActionTypes.SURVEY_LIST_COLUMNS_UPDATE:
			return {
				...state,
				columns: action.payload,
			};

		// Clone Survey
		case ActionTypes.SURVEY_LIST_CLONE_SURVEY_REQUESTED:
			return {
				...state,
				loading: true,
			};
		case ActionTypes.SURVEY_LIST_CLONE_SURVEY_SUCCEEDED: {
			const clones: IValidationModel<ISurveyListItem>[] = action.payload.map((c) => {
				return {
					subject: c,
					isValid: true,
				};
			});
			const surveyList = [...clones, ...state.surveys];
			return {
				...state,
				loading: false,
				surveys: surveyList,
			};
		}
		case ActionTypes.SURVEY_LIST_UPDATE_SURVEY_NAME: {
			const surveys = _cloneDeep(state.surveys);
			const index = surveys.findIndex((survey) => {
				return survey.subject.surveyId === action.payload.subject.surveyId;
			});
			// Nothing to change if the target survey is not found in the collection.
			if (index < 0) {
				return { ...state };
			}
			if (action.payload.isValid) {
				surveys[index] = action.payload;
			} else {
				surveys[index].isValid = false;
				surveys[index].validationResults = action.payload.validationResults;
			}
			return {
				...state,
				surveys: surveys,
			};
		}

		case ActionTypes.SURVEY_LIST_CLEAR_VALIDATION: {
			const surveys = _cloneDeep(state.surveys);
			const index = surveys.findIndex((survey) => {
				return survey.subject.surveyId === action.payload.surveyId;
			});
			// Nothing to change if the target survey is not found in the collection.
			if (index < 0) {
				return { ...state };
			}
			surveys[index].subject = action.payload;
			surveys[index].isValid = true;
			surveys[index].validationResults = undefined;
			return {
				...state,
				surveys: surveys,
			};
		}

		case ActionTypes.SIGNALR_SURVEY_DELETE_CHANGED: {
			return { ...state, deleteStatusChange: action.payload };
		}

		case ActionTypes.SURVEY_LIST_DELETE_SURVEY_FAILED: {
			const surveys = _cloneDeep(state.surveys);
			const index = surveys.findIndex((survey) => {
				return survey.subject.surveyId === action.payload;
			});
			// Nothing to change if the target survey is not found in the collection.
			if (index < 0) {
				return { ...state };
			}
			surveys[index].subject.surveyStatus = SurveyStatusType.Draft;
			return {
				...state,
				surveys: surveys,
			};
		}

		case ActionTypes.SURVEY_LIST_DELETE_SURVEY_REQUESTED: {
			const surveys = _cloneDeep(state.surveys);
			const index = surveys.findIndex((survey) => {
				return survey.subject.surveyId === action.payload;
			});
			// Nothing to change if the target survey is not found in the collection.
			if (index < 0) {
				return { ...state };
			}
			surveys[index].subject.surveyStatus = SurveyStatusType.DeletingInProgress;
			return {
				...state,
				surveys: surveys,
			};
		}

		case ActionTypes.SURVEY_LIST_DELETE_SURVEY_SUCCEEDED: {
			return { ...state };
		}
	}
	return state;
};
